import { action, observable, makeObservable } from 'mobx';
import pick from 'lodash.pick';
import { AxiosResponse } from 'axios';

import URLS from 'jsx/constants/urls';
import { get, patch, post, put } from 'jsx/utils/axiosWrapper';
import { identifyUser } from 'jsx/utils/metrics';
import PatientModel, { UserResourceType } from 'jsx/models/patient';
import { ProductModel } from 'jsx/models/product';
import { Initializable } from 'jsx/mixins';
import BaseStore from 'jsx/stores/baseStore';
import useStores from 'jsx/utils/hooks/useStores';

type Cohort = (
  experimentShortName: string,
  cohortOverride?: string | null,
  reassignCohort?: boolean,
) => Promise<PatientModel>;

class BaseUserStore extends Initializable(BaseStore) {
  @observable
  patient = new PatientModel();

  @observable
  isHydrated = false;

  onNextHydrationQueue: ((
    this: BaseUserStore,
    userStore: BaseUserStore,
  ) => unknown)[] = [];

  onInitialize = async () => {
    const resp = await get<{ data: UserResourceType }>(URLS.api.user());

    const userData = resp.data.data;

    this.hydrate(userData);

    identifyUser({ patient: this.patient });
  };

  constructor() {
    super();
    makeObservable(this);
  }

  /**
   * This helper method was originally used to safely access properties.
   * Prefer pulling properties from patient directly.
   *
   * @deprecated
   */
  get<T extends keyof PatientModel>(property: T) {
    return this.patient[property];
  }

  /**
   * gets returns Partial<PatientModel>, which is liable to raise type errors in usage
   *
   * Prefer destructuring properties from patient directly instead of using gets
   *
   * @deprecated
   */
  gets<T extends string[]>(properties: T) {
    return this.patient ? pick(this.patient, properties) : {};
  }

  fetch = () =>
    get<{ data: UserResourceType }>(URLS.api.user()).then((response) => {
      this.hydrate(response.data.data);
    });

  /**
   * @deprecated Prefer direct access `patient.next_shipment_ships_at?.format(format);`
   */
  format(property: string, format: string) {
    if (!this.patient) {
      return undefined;
    }

    return this.patient.format(property, format);
  }

  @action
  set<T extends keyof PatientModel>(property: T, value: PatientModel[T]) {
    this.patient[property] = value;
  }

  @action
  hydrate(patient: UserResourceType) {
    this.patient = new PatientModel(patient);
    this.isHydrated = true;
    this.onNextHydration();
    return this.patient;
  }

  onNextHydration() {
    while (this.onNextHydrationQueue.length) {
      const cb = this.onNextHydrationQueue.splice(0, 1)[0];
      cb.call(this, this);
    }
  }

  @action
  clear() {
    this.patient = new PatientModel();
  }

  @action
  public changeSubscriptionItems = (
    subscriptionItems: { itemId: number; quantity: number }[],
    cancelTypeId?: number,
    deferredProducts: ProductModel[] = [],
  ): Promise<PatientModel> => {
    const items = subscriptionItems.map((subItem) => ({
      item_id: subItem.itemId,
      quantity: subItem.quantity,
    }));

    const deferred_products = deferredProducts.map((product: ProductModel) => ({
      product_id: product.id,
    }));

    return put<{ data: { user: UserResourceType } }>(
      URLS.api.changeSubscriptionItems(),
      { items, cancelTypeId, deferred_products },
    ).then((res) => this.hydrate(res.data.data.user));
  };

  @action
  public removePatientProductDeferrals = (): Promise<PatientModel> => {
    return put<{ data: { user: UserResourceType } }>(
      URLS.api.removePatientProductDeferrals(),
      {},
    ).then((res) => this.hydrate(res.data.data.user));
  };

  @action
  cohort: Cohort = (
    experimentShortName,
    cohortOverride = null,
    reassignCohort = false,
  ) => {
    const data = { experimentShortName, cohortOverride, reassignCohort };

    return post<{ data: UserResourceType }>(
      URLS.api.experimentCohort(),
      data,
    ).then((res) => this.hydrate(res.data.data));
  };

  @action
  updatePhoneNumber = (data: { phone: string }) =>
    patch(URLS.api.phoneNumber(), data).then(
      (response: AxiosResponse<{ user: UserResourceType }>) => {
        this.hydrate(response.data.user);
      },
    );
}

/**
 * You should generally prefer `useUserStore` or `useSignupUserStore`, but if you
 * need to write a component that works with either you can use this hook instead.
 */
export const useBaseUserStore = (): { userStore: BaseUserStore } => {
  const { userStore } = useStores();

  return { userStore };
};

export default BaseUserStore;
